import {
  Cart,
  CartPositionType,
  Checkout,
  CheckoutFactory,
  IAddressData,
  ICartPosition,
  ICountriesLookup,
} from "@connox/typescript-library";
import {getShopId} from "../../components/lib/getShopId";
import {
  INTERNAL_ERROR_TYPE,
  isoLocales,
  PaymentController,
  Payments,
} from "../../components/lib/payments/PaymentController";
import {IGetCheckoutShippingOptionsData} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import React from "react";
import {splitStreetAndNumber} from "../paypal/paypalAddressHelpers";
import {AddressResponseData} from "@connox/typescript-library/types/checkout/AddressResponseData";
import i18n from "i18next";
import braintree, {googlePayment} from "braintree-web";
import {GPayAuthError} from "./onGoogleAuthorized";
import formatCurrency from "../formatCurrency";

const merchantId = __PRODUCTION__ ? "BCR2DN4TXX43RTJW" : "12345678901234567890";
const googleMerchantId = __PRODUCTION__ ? merchantId : "TEST";

/**
 * Type adapter for `positionToDisplayItem()`
 */
const typeAdapter: Record<CartPositionType, google.payments.api.DisplayItemType> = {
  Item: "LINE_ITEM",
  Shipping: "SHIPPING_OPTION",
  CashDiscount: "DISCOUNT",
  Discount: "DISCOUNT",
  Voucher: "DISCOUNT",
  Point: "DISCOUNT",
  Fee: "TAX",
};

type ExtendedRequestUpdateType = {
  isPhoneRequired?: boolean | undefined;
  requestUpdate: google.payments.api.PaymentDataRequestUpdate;
};

let globalShippingOptions: IGetCheckoutShippingOptionsData[] = [];

/**
 * Adapter method to convert cart positions to gpay `displayItems` via Array.map()
 *
 * @param pos single cart position
 */
export const positionToDisplayItem = (pos: ICartPosition): google.payments.api.DisplayItem => ({
  type: typeAdapter[pos.type],
  label: pos.type === "Item" ? `${pos.quantity.toString()} * ${pos.text}` : pos.text,
  price: pos.amount.amount,
});

type ReturnType = {
  gPayInstance: braintree.GooglePayment;
  paymentDataRequest: google.payments.api.PaymentDataRequest;
};

export const preparePaymentDataRequest = async (
  braintreeToken: string,
  cart: Cart,
  countries: ICountriesLookup[],
): Promise<ReturnType> => {
  const clientInstance = await braintree.client.create({authorization: braintreeToken});
  const gPayInstance = await googlePayment.create({
    client: clientInstance,
    googlePayVersion: 2,
    googleMerchantId,
  });

  const tempPaymentDataRequest = await gPayInstance.createPaymentDataRequest({
    transactionInfo: {
      currencyCode: cart.currencyCode,
      totalPriceStatus: "ESTIMATED",
      totalPrice: cart.valuePayable.amount,
    },
    emailRequired: true,
  });

  if (!tempPaymentDataRequest.allowedPaymentMethods || !tempPaymentDataRequest.allowedPaymentMethods.length) {
    throw new GPayAuthError(
      "No allowed payment methods returned from gpay",
      INTERNAL_ERROR_TYPE.GOOGLE_PAY.OnPurchase + "Error",
    );
  }

  const allowedPaymentMethods = tempPaymentDataRequest.allowedPaymentMethods.map((apm) => {
    apm.parameters.billingAddressRequired = true;
    apm.parameters.billingAddressParameters = {format: "FULL"};
    if (
      window.c_ActiveCurrency === "EUR" &&
      apm.type === "CARD" &&
      !apm.parameters.allowedCardNetworks.includes("AMEX")
    ) {
      apm.parameters.allowedCardNetworks.push("AMEX");
    }

    return apm;
  });

  const paymentDataRequest: google.payments.api.PaymentDataRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    emailRequired: true,
    callbackIntents: ["SHIPPING_ADDRESS", "PAYMENT_AUTHORIZATION", "SHIPPING_OPTION"],
    shippingAddressRequired: true,
    shippingOptionRequired: true,
    shippingAddressParameters: {
      allowedCountryCodes: countries.map((country) => country.countryCode),
    },
    allowedPaymentMethods,

    merchantInfo: {
      merchantId,
      merchantName: "connox - be unique",
    },
    transactionInfo: {
      ...tempPaymentDataRequest.transactionInfo,
      totalPrice: cart.valuePayable.amount,
      totalPriceLabel: i18n.t<string>("cart.total"),
      checkoutOption: "DEFAULT",
      countryCode: isoLocales[getShopId()].split("_")[1],
      displayItems: cart.positions.map(positionToDisplayItem),
    },
  };

  return {
    gPayInstance,
    paymentDataRequest,
  };
};

/**
 * initialize process on payment data change with `callbackTrigger === "INITIALIZE"`
 * @param cart cart instance from save cart context
 * @param intermediatePaymentData changed payment data
 * @param paymentRequest paymentRequest state
 * @param checkoutRef reference that is to be set to `checkout` object
 */
export const initialize = async (
  cart: Cart,
  intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  paymentRequest: google.payments.api.PaymentDataRequest,
  checkoutRef: React.MutableRefObject<Checkout | undefined>,
): Promise<ExtendedRequestUpdateType> => {
  if (cart.countryCode !== intermediatePaymentData.shippingAddress?.countryCode) {
    await cart.put({countryCode: intermediatePaymentData.shippingAddress?.countryCode});
  }
  const checkout = await CheckoutFactory.generateCheckout(getShopId(), cart);
  checkoutRef.current = checkout;
  await checkout.put({paymentId: Payments.GooglePay});
  PaymentController.createPayment(checkout, Payments.GooglePay);

  return await processShippingOptions(
    checkout,
    cart,
    paymentRequest.transactionInfo,
    intermediatePaymentData.shippingOptionData?.id,
  );
};

/**
 * Set new shipping address, and retrieve new shipping options, when address is set to new country code
 * @param cart cart instance from save cart context
 * @param checkout checkout from reference
 * @param intermediatePaymentData changed payment data
 * @param paymentRequest paymentRequest state
 */
export const handleShippingAddress = async (
  cart: Cart,
  checkout: Checkout,
  intermediatePaymentData: google.payments.api.IntermediatePaymentData,
  paymentRequest: google.payments.api.PaymentDataRequest,
): Promise<ExtendedRequestUpdateType> => {
  if (cart.countryCode !== intermediatePaymentData.shippingAddress?.countryCode) {
    await cart.put({countryCode: intermediatePaymentData.shippingAddress?.countryCode});

    const {isPhoneRequired, requestUpdate} = await processShippingOptions(
      checkout,
      cart,
      paymentRequest.transactionInfo,
      intermediatePaymentData.shippingOptionData?.id,
    );

    if (!requestUpdate.newShippingOptionParameters?.shippingOptions.length) {
      requestUpdate.error = {
        intent: "SHIPPING_ADDRESS",
        reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
        message: i18n.t("checkout.noShippingToSelectedAddress"),
      };
    }

    return {
      isPhoneRequired,
      requestUpdate,
    };
  }

  return {
    isPhoneRequired: !!paymentRequest.shippingAddressParameters?.phoneNumberRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: buildShippingOptions(globalShippingOptions, cart),
        defaultSelectedOptionId: intermediatePaymentData.shippingOptionData?.id,
      },
    },
  };
};

/**
 * Set new selected shipping option alongside updated cart total and display items
 * @param checkout
 * @param paymentRequest
 * @param shippingOptionData
 */
export const setNewShippingOption = async (
  checkout: Checkout,
  paymentRequest: google.payments.api.PaymentDataRequest,
  shippingOptionData: google.payments.api.SelectionOptionData,
): Promise<ExtendedRequestUpdateType> => {
  const logisticsTypeId = Number(shippingOptionData.id);
  await checkout.put({
    logisticsTypeId,
  });
  const isPhoneRequired = globalShippingOptions.find(
    (ship) => ship.logisticsTypeId === logisticsTypeId,
  )?.isPhoneRequired;

  return {
    isPhoneRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: paymentRequest.shippingOptionParameters?.shippingOptions ?? [],
        defaultSelectedOptionId: shippingOptionData.id,
      },
      newTransactionInfo: {
        ...paymentRequest.transactionInfo,
        displayItems: checkout.cartData.positions.map(positionToDisplayItem),
        totalPrice: checkout.cartData.value.payable.amount,
      },
    },
  };
};

/**
 * Get new shipping options, pick new default shipping, calculate new cart value
 *
 * @param checkout needed for `.getAvailableShippingOptions()`
 * @param cart needed for item positions and total amount
 * @param oldTransactionInfo needed for object destructuring in return value
 * @param shippingOptionDataId possibly formerly set shipping option
 */
const processShippingOptions = async (
  checkout: Checkout,
  cart: Cart,
  oldTransactionInfo: google.payments.api.TransactionInfo,
  shippingOptionDataId: string | undefined,
): Promise<ExtendedRequestUpdateType> => {
  globalShippingOptions = await checkout.getAvailableShippingOptions();
  const defaultSelectedOption = getDefaultShippingOption(globalShippingOptions, shippingOptionDataId);

  if (!defaultSelectedOption) {
    return {
      requestUpdate: {
        error: {
          reason: "SHIPPING_ADDRESS_UNSERVICEABLE",
          intent: "SHIPPING_OPTION",
          message: i18n.t("checkout.noShippingToSelectedAddress"),
        },
        newShippingOptionParameters: {
          shippingOptions: [],
        },
        newTransactionInfo: {
          ...oldTransactionInfo,
          displayItems: cart.positions.map(positionToDisplayItem),
          totalPrice: cart.valuePayable.amount,
        },
      },
    };
  }
  const defaultSelectedOptionId = defaultSelectedOption.logisticsTypeId.toString();

  return {
    isPhoneRequired: defaultSelectedOption.isPhoneRequired,
    requestUpdate: {
      newShippingOptionParameters: {
        shippingOptions: buildShippingOptions(globalShippingOptions, cart),
        defaultSelectedOptionId,
      },
      newTransactionInfo: {
        ...oldTransactionInfo,
        displayItems: cart.positions.map(positionToDisplayItem),
        totalPrice: cart.valuePayable.amount,
      },
    },
  };
};

/**
 * Add some metadata to error for handling in Sentry
 *
 * @param err error from gpay
 */
export const prepareError = (err: google.payments.api.PaymentsError | Error): Error => {
  const {statusCode, statusMessage} = err as google.payments.api.PaymentsError;
  let gPayError: Error | undefined;
  if (statusCode || statusMessage) {
    gPayError = new Error(statusMessage || statusCode);
    gPayError.name = "Internal" + gPayError.name;
    (gPayError as Error & {gPayStatusCode: string}).gPayStatusCode = statusCode;
  }
  const errorToSend = gPayError || (err as Error);
  errorToSend.name = "GooglePay" + errorToSend.name;

  return errorToSend;
};

/**
 * Shipping options adapter method for `.map()`
 *
 * @param shippingOptions our shipping options to adapt to gpay shipping options
 * @param cart base for shipping price calculation
 */

const buildShippingOptions = (
  shippingOptions: IGetCheckoutShippingOptionsData[],
  cart: Cart,
): google.payments.api.SelectionOption[] => {
  return shippingOptions.map((shippingOption) => {
    let formattedAmount: string;
    if (shippingOption.amount.amount === "0.00") {
      formattedAmount = i18n.t("general.free");
    } else if (cart.isNet) {
      formattedAmount = formatCurrency({
        value: Number(shippingOption.amount.amount) / 1.19,
        currencyCode: cart.currencyCode,
      });
    } else {
      formattedAmount = shippingOption.amount.formatted;
    }

    return {
      id: shippingOption.logisticsTypeId.toString(),
      label: `${shippingOption.name} (${formattedAmount})`,
      description: shippingOption.description ?? undefined,
    };
  });
};

/**
 * return default shipping option
 * @param shippingOptions
 * @param previousOptionId
 */
const getDefaultShippingOption = (
  shippingOptions: IGetCheckoutShippingOptionsData[],
  previousOptionId: string | undefined,
): IGetCheckoutShippingOptionsData | undefined => {
  return (
    shippingOptions.find((so) => so.logisticsTypeId.toString() === previousOptionId) ||
    shippingOptions.find((so) => so.isCustomerPreferredShipping) ||
    shippingOptions.find((so) => so.isBestMatch)
  );
};

export const googleToInternalAddress = (
  gPayAddress: google.payments.api.Address,
  email: string,
  checkoutAddress: AddressResponseData | null,
): IAddressData => {
  const {countryCode, postalCode, phoneNumber, locality, administrativeArea, name, address1} = gPayAddress;
  const recipientNameArray = name?.split(" ") ?? ["", ""];
  const lastName = recipientNameArray.pop();
  const firstName = recipientNameArray.join(" ");
  const [street, streetNumber] = splitStreetAndNumber(address1 ?? "", countryCode);

  return {
    company: checkoutAddress?.company || null,
    dateOfBirth: checkoutAddress?.dateOfBirth || null,
    department: checkoutAddress?.department || null,
    title: checkoutAddress?.title || null,
    gender: checkoutAddress?.gender || "F",
    firstName,
    lastName: lastName || "",
    street,
    number: streetNumber,
    zip: postalCode,
    city: locality,
    state: administrativeArea || null,
    countryCode,
    email,
    phone: phoneNumber ?? null,
  };
};
